import { numberToDollars } from '../../../../utils'

// Builds the AdviceDataTable columns/rows for the Goals step. The step is
// read-only: it reflects the goals the user has told us about and whether each
// one is currently included in planning. Callers pass the current (non-past)
// goals and the ids of the goals included in the active engine case.
//
// "Amount needed" is each goal's total still needed — it sums every year of a
// multi-year goal but excludes any amount already withdrawn (see Goal.totalRemaining).

/**
 * Goal-date cell: a single year for one-time goals, or a "start-end" range for
 * multi-year goals.
 */
export function formatGoalDate(goal) {
  return goal.type ? `${goal.earliestYear}` : `${goal.earliestYear}-${goal.latestYear}`
}

/** Dollar cell/heading value, e.g. 75000 -> "$75,000". */
export function formatAmount(value) {
  return `$${numberToDollars(value, true)}`
}

export const isIncluded = includedIds => goal => includedIds.includes(goal.id)

/**
 * Goals in display order: included first, then excluded. Each group keeps the
 * incoming order — callers pass goals already sorted by date (earliest first),
 * so within a group they stay earliest-to-latest.
 */
export function orderGoals({ goals, includedIds }) {
  const included = isIncluded(includedIds)
  return [...goals.filter(included), ...goals.filter(goal => !included(goal))]
}

/**
 * True when at least one of the given goals is included. The Goals step only
 * appears when there's an included (non-past) goal; Advice uses this to decide
 * whether to skip/guard the step.
 */
export function hasIncludedGoals({ goals, includedIds }) {
  return goals.some(isIncluded(includedIds))
}

/**
 * Combined amount still needed across the included goals — the total shown in
 * the heading. Excludes already-withdrawn amounts and includes every year of a
 * multi-year goal (both via Goal.totalRemaining).
 */
export function includedGoalsTotal({ goals, includedIds }) {
  return goals.filter(isIncluded(includedIds)).reduce((sum, goal) => sum + goal.totalRemaining, 0)
}

// AdviceDataTable appends the Included? column, deriving it from each row's `included` flag.
export function buildColumns() {
  return [
    { key: 'name', label: 'Name' },
    { key: 'goalDate', label: 'Goal date' },
    { key: 'amount', label: 'Amt needed', align: 'right' },
  ]
}

export function buildRows({ goals, includedIds }) {
  return orderGoals({ goals, includedIds }).map(goal => ({
    id: goal.id,
    included: includedIds.includes(goal.id),
    values: {
      name: goal.name,
      goalDate: formatGoalDate(goal),
      amount: formatAmount(goal.totalRemaining),
    },
  }))
}
